import { HttpErrorResponse, HttpRequest } from '@angular/common/http';

import {
  ExportJob,
  Kpi,
  LedgerAccount,
  LedgerAccountCreate,
  LedgerAccountTypeEnum,
  LedgerAccountUpdate,
  LedgerAccountUpdateResponse,
  LedgerAccountsResponse,
  LedgerExportStatusEnum,
  LedgerKpiValueTypeEnum,
  LedgerNormalBalanceEnum,
} from '../../../cfo/models/ledger.models';
import { getSubcategoryLabel } from '../../../cfo/utils/chart-of-accounts.util';
import { ID } from '../../../models/id';
import { formatCents, getAccountActivity } from './ledger-mock-entries';
import { MOCK_SUBCATEGORY_OPTIONS } from './ledger-mock-subcategories';
import { MockResponse, ResponseStateEnum } from './mock.models';

interface AccountRecord extends Omit<LedgerAccount, 'balance' | 'children'> {
  openingCents: number;
}

type AccountGroup = Pick<AccountRecord, 'type' | 'category' | 'subcategory' | 'normal_balance'>;

const LEDGER_PATH = '/cru/ledger';
const ACCOUNTS_PATH = `${LEDGER_PATH}/accounts/`;
const ACCOUNT_ID_PATTERN = /\/accounts\/(\d+)\/$/;
const ACCOUNT_TYPE_BASE_NUMBERS: Record<LedgerAccountTypeEnum, number> = {
  [LedgerAccountTypeEnum.ASSET]: 1000,
  [LedgerAccountTypeEnum.LIABILITY]: 2000,
  [LedgerAccountTypeEnum.EQUITY]: 3000,
  [LedgerAccountTypeEnum.REVENUE]: 4000,
  [LedgerAccountTypeEnum.EXPENSE]: 5000,
};
const ROOT_NUMBER_STEP = 10;
const ACTIVE_EXPORT_ID = 7001;
const ARCHIVED_EXPORT_ID = 7002;

function familyGroup(subcategory: string, category: string): AccountGroup {
  const family = MOCK_SUBCATEGORY_OPTIONS.find((option) => option.value === subcategory);
  if (!family) {
    throw new Error(`Mock ledger subcategory ${subcategory} does not exist`);
  }
  return { type: family.type, category, subcategory, normal_balance: family.normal_balance };
}

const CASH = familyGroup('asset.cash', 'current_assets');
const RECEIVABLES = familyGroup('asset.receivables', 'current_assets');
const FIXED_ASSETS = familyGroup('asset.fixed_assets', 'fixed_assets');
const PAYABLES = familyGroup('liability.payables', 'current_liabilities');
const PAYROLL_LIABILITIES = familyGroup('liability.payroll_liabilities', 'current_liabilities');
const SHORT_TERM_DEBT = familyGroup('liability.short_term_debt', 'current_liabilities');
const LONG_TERM_DEBT = familyGroup('liability.long_term_debt', 'long_term_liabilities');
const CONTRIBUTED_CAPITAL = familyGroup('equity.contributed_capital', 'equity');
const RETAINED_EARNINGS = familyGroup('equity.retained_earnings', 'equity');
const CONTRACT_REVENUE = familyGroup('revenue.contract_revenue', 'revenue');
const LABOR = familyGroup('direct_cost.labor', 'operating_expenses');
const MATERIALS = familyGroup('direct_cost.materials', 'operating_expenses');
const SUBCONTRACTORS = familyGroup('direct_cost.subcontractors', 'operating_expenses');
const INSURANCE = familyGroup('expense.insurance', 'operating_expenses');

function rootAccount(
  id: ID,
  number: number,
  position: number,
  name: string,
  group: AccountGroup,
  extra: Partial<AccountRecord> = {},
): AccountRecord {
  return {
    id,
    number,
    sub_number: null,
    code: String(number),
    position,
    name,
    ...group,
    parent_id: null,
    depth: 0,
    is_system: false,
    archived: false,
    openingCents: 0,
    ...extra,
  };
}

function subAccount(
  id: ID,
  parent: AccountRecord,
  subNumber: number,
  name: string,
  extra: Partial<AccountRecord> = {},
): AccountRecord {
  return {
    id,
    number: parent.number,
    sub_number: subNumber,
    code: `${parent.number}-${subNumber}`,
    position: subNumber,
    name,
    type: parent.type,
    category: parent.category,
    subcategory: parent.subcategory,
    normal_balance: parent.normal_balance,
    parent_id: parent.id,
    depth: 1,
    is_system: false,
    archived: false,
    openingCents: 0,
    ...extra,
  };
}

function buildSeedRecords(): AccountRecord[] {
  const cash = rootAccount(1, 1000, 1, 'Cash', CASH);
  const equipment = rootAccount(5, 1500, 3, 'Construction Equipment', FIXED_ASSETS);
  const payrollLiabilities = rootAccount(10, 2300, 6, 'Payroll Liabilities', PAYROLL_LIABILITIES);
  const contractRevenue = rootAccount(17, 4000, 11, 'Contract Revenue', CONTRACT_REVENUE);
  const insurance = rootAccount(23, 6100, 15, 'Insurance', INSURANCE);

  return [
    cash,
    subAccount(2, cash, 1, 'Operating Checking', { openingCents: 8_500_000 }),
    subAccount(3, cash, 2, 'Payroll Checking', { archived: true }),
    rootAccount(4, 1100, 2, 'Accounts Receivable', RECEIVABLES, { openingCents: 5_200_000 }),
    equipment,
    subAccount(6, equipment, 1, 'Heavy Equipment', { openingCents: 18_000_000 }),
    subAccount(7, equipment, 2, 'Trucks and Vehicles', { openingCents: 9_500_000 }),
    rootAccount(8, 1580, 4, 'Old Vehicles', FIXED_ASSETS, { archived: true }),
    rootAccount(9, 2000, 5, 'Accounts Payable', PAYABLES, { openingCents: 2_100_000 }),
    payrollLiabilities,
    subAccount(11, payrollLiabilities, 1, 'Payroll Taxes Payable', { openingCents: 300_000 }),
    subAccount(12, payrollLiabilities, 2, 'Workers Comp Payable', { openingCents: 150_000 }),
    rootAccount(13, 2500, 7, 'Equipment Loan', LONG_TERM_DEBT, { openingCents: 14_000_000 }),
    rootAccount(14, 2900, 8, 'Old Line of Credit', SHORT_TERM_DEBT, { archived: true }),
    rootAccount(15, 3000, 9, "Owner's Capital", CONTRIBUTED_CAPITAL, { openingCents: 10_000_000 }),
    rootAccount(16, 3900, 10, 'Retained Earnings', RETAINED_EARNINGS, {
      openingCents: 14_650_000,
      is_system: true,
    }),
    contractRevenue,
    subAccount(18, contractRevenue, 1, 'Commercial Contracts'),
    subAccount(19, contractRevenue, 2, 'Residential Contracts'),
    rootAccount(20, 5000, 12, 'Direct Labor', LABOR),
    rootAccount(21, 5100, 13, 'Materials', MATERIALS),
    rootAccount(22, 5200, 14, 'Subcontractors', SUBCONTRACTORS),
    insurance,
    subAccount(24, insurance, 1, 'General Liability'),
    subAccount(25, insurance, 2, 'Workers Comp Insurance'),
  ];
}

const accountRecords: AccountRecord[] = buildSeedRecords();

function byPosition(a: AccountRecord, b: AccountRecord): number {
  return a.position - b.position;
}

function childrenOf(record: AccountRecord): AccountRecord[] {
  return accountRecords.filter((candidate) => candidate.parent_id === record.id);
}

function rolledUpBalanceCents(record: AccountRecord): number {
  const { debitCents, creditCents } = getAccountActivity(record.id);
  const activity =
    record.normal_balance === LedgerNormalBalanceEnum.DEBIT ? debitCents - creditCents : creditCents - debitCents;
  return childrenOf(record).reduce((sum, child) => sum + rolledUpBalanceCents(child), record.openingCents + activity);
}

function toAccount(record: AccountRecord, children: LedgerAccount[] = []): LedgerAccount {
  const { openingCents, ...account } = record;
  return { ...account, balance: formatCents(rolledUpBalanceCents(record)), children };
}

function buildTree(parentId: ID | null, includeArchived: boolean): LedgerAccount[] {
  return accountRecords
    .filter((record) => record.parent_id === parentId && (includeArchived || !record.archived))
    .sort(byPosition)
    .map((record) => toAccount(record, buildTree(record.id, includeArchived)));
}

function flattenAccounts(accounts: LedgerAccount[]): LedgerAccount[] {
  return accounts.flatMap((account) => [account, ...flattenAccounts(account.children)]);
}

function readAccountsQuery(request: HttpRequest<unknown>): {
  includeArchived: boolean;
  flat: boolean;
  type: string | null;
} {
  const { searchParams } = new URL(request.url);
  return {
    includeArchived: searchParams.get('include_archived') === 'true',
    flat: searchParams.get('flat') === 'true',
    type: searchParams.get('type'),
  };
}

function totalBalanceCents(type: LedgerAccountTypeEnum): number {
  return accountRecords
    .filter((record) => record.parent_id === null && !record.archived && record.type === type)
    .reduce((sum, record) => sum + rolledUpBalanceCents(record), 0);
}

function buildKpis(): Kpi[] {
  const assets = totalBalanceCents(LedgerAccountTypeEnum.ASSET);
  const liabilities = totalBalanceCents(LedgerAccountTypeEnum.LIABILITY);
  return [
    {
      key: 'total_assets',
      title: 'Total assets',
      amount: formatCents(assets),
      value_type: LedgerKpiValueTypeEnum.CURRENCY,
    },
    {
      key: 'total_liabilities',
      title: 'Total liabilities',
      amount: formatCents(liabilities),
      value_type: LedgerKpiValueTypeEnum.CURRENCY,
    },
    {
      key: 'total_equity',
      title: 'Total equity',
      amount: formatCents(assets - liabilities),
      value_type: LedgerKpiValueTypeEnum.CURRENCY,
    },
  ];
}

function listAccounts(request: HttpRequest<unknown>): LedgerAccount[] {
  const { includeArchived, flat, type } = readAccountsQuery(request);
  const tree = buildTree(null, includeArchived).filter((account) => !type || account.type === type);
  return flat ? flattenAccounts(tree).map((account) => ({ ...account, children: [] })) : tree;
}

function handleGetAccounts(request: HttpRequest<unknown>): LedgerAccountsResponse {
  return { kpis: buildKpis(), accounts: listAccounts(request), subcategories: MOCK_SUBCATEGORY_OPTIONS };
}

function toCsvRow(account: LedgerAccount): string {
  return [
    account.code,
    `"${account.name}"`,
    account.type,
    `"${getSubcategoryLabel(account.subcategory, MOCK_SUBCATEGORY_OPTIONS)}"`,
    account.balance,
  ].join(',');
}

function createCsvUrl(rows: string[]): string {
  return URL.createObjectURL(new Blob([rows.join('\n')], { type: 'text/csv' }));
}

function buildAccountsCsvUrl(includeArchived: boolean): string {
  const accounts = flattenAccounts(buildTree(null, includeArchived));
  return createCsvUrl(['Code,Name,Type,Subcategory,Balance', ...accounts.map(toCsvRow)]);
}

function buildReadyExport(id: ID, includeArchived: boolean): ExportJob {
  return { id, status: LedgerExportStatusEnum.READY, url: buildAccountsCsvUrl(includeArchived) };
}

function handleAccountsExport(request: HttpRequest<unknown>): ExportJob {
  const { includeArchived } = readAccountsQuery(request);
  return buildReadyExport(includeArchived ? ARCHIVED_EXPORT_ID : ACTIVE_EXPORT_ID, includeArchived);
}

function inferAccountType(payload: LedgerAccountCreate): LedgerAccountTypeEnum {
  if (payload.type) {
    return payload.type;
  }
  const family = MOCK_SUBCATEGORY_OPTIONS.find((option) => option.value === payload.subcategory);
  if (family) {
    return family.type;
  }
  return payload.normal_balance === LedgerNormalBalanceEnum.DEBIT
    ? LedgerAccountTypeEnum.ASSET
    : LedgerAccountTypeEnum.LIABILITY;
}

function categoryFor(payload: LedgerAccountCreate, type: LedgerAccountTypeEnum): string {
  const sameSubcategory = accountRecords.find(
    (record) => record.type === type && record.subcategory === payload.subcategory,
  );
  return sameSubcategory?.category ?? payload.subcategory;
}

function nextId(): ID {
  return Math.max(...accountRecords.map((record) => record.id)) + 1;
}

function invalidParent(detail: string): HttpErrorResponse {
  return new HttpErrorResponse({ status: 400, error: { detail } });
}

function findParent(payload: LedgerAccountCreate): AccountRecord | null {
  if (payload.parent_id === undefined || payload.parent_id === null) {
    return null;
  }
  const parent = accountRecords.find((record) => record.id === payload.parent_id);
  if (!parent) {
    throw invalidParent('The parent does not exist');
  }
  if (parent.parent_id !== null) {
    throw invalidParent('The parent is itself a sub-account');
  }
  if (parent.subcategory !== payload.subcategory) {
    throw invalidParent("A sub-account must be in its parent's family");
  }
  return parent;
}

function insertSubAccount(parent: AccountRecord, payload: LedgerAccountCreate): AccountRecord {
  const subNumber = Math.max(0, ...childrenOf(parent).map((child) => child.sub_number ?? 0)) + 1;
  return subAccount(nextId(), parent, subNumber, payload.name);
}

function insertRootAccount(payload: LedgerAccountCreate): AccountRecord {
  const type = inferAccountType(payload);
  const roots = accountRecords.filter((record) => record.parent_id === null);
  const sameType = roots.filter((root) => root.type === type);
  const base = ACCOUNT_TYPE_BASE_NUMBERS[type];
  const preceding = sameType.length ? sameType : roots.filter((root) => root.number < base);
  const position = Math.max(0, ...preceding.map((root) => root.position)) + 1;
  roots
    .filter((root) => root.position >= position)
    .forEach((root) => Object.assign(root, { position: root.position + 1 }));
  const number = sameType.length ? Math.max(...sameType.map((root) => root.number)) + ROOT_NUMBER_STEP : base;
  const group: AccountGroup = {
    type,
    category: categoryFor(payload, type),
    subcategory: payload.subcategory,
    normal_balance: payload.normal_balance,
  };
  return rootAccount(nextId(), number, position, payload.name, group);
}

function handleCreateAccount(request: HttpRequest<unknown>): LedgerAccount {
  const payload = request.body as LedgerAccountCreate;
  const parent = findParent(payload);
  const record = parent ? insertSubAccount(parent, payload) : insertRootAccount(payload);
  accountRecords.push(record);
  return toAccount(record);
}

function snapshotOrdering(): Map<ID, string> {
  return new Map(accountRecords.map(({ id, position, code }) => [id, `${position}|${code}`]));
}

function moveToPosition(record: AccountRecord, position: number): void {
  const siblings = accountRecords
    .filter((candidate) => candidate.parent_id === record.parent_id && candidate.id !== record.id)
    .sort(byPosition);
  siblings.splice(Math.min(Math.max(position - 1, 0), siblings.length), 0, record);
  siblings.forEach((sibling, index) => Object.assign(sibling, { position: index + 1 }));
}

function renumberAccounts(): void {
  const roots = accountRecords.filter((record) => record.parent_id === null).sort(byPosition);
  const numbers = roots.map((root) => root.number).sort((a, b) => a - b);
  roots.forEach((root, index) => {
    const number = numbers[index];
    Object.assign(root, { number, code: String(number) });
    childrenOf(root)
      .sort(byPosition)
      .forEach((child, childIndex) =>
        Object.assign(child, { number, sub_number: childIndex + 1, code: `${number}-${childIndex + 1}` }),
      );
  });
}

function handleUpdateAccount(request: HttpRequest<unknown>): LedgerAccountUpdateResponse {
  const id = Number(ACCOUNT_ID_PATTERN.exec(new URL(request.url).pathname)?.[1]);
  const update = request.body as LedgerAccountUpdate;
  const record = accountRecords.find((candidate) => candidate.id === id);
  if (!record) {
    throw new Error(`Mock ledger account ${id} does not exist`);
  }
  const before = snapshotOrdering();

  record.name = update.name ?? record.name;
  record.archived = update.archived ?? record.archived;
  if (update.position !== undefined) {
    moveToPosition(record, update.position);
    renumberAccounts();
  }

  const after = snapshotOrdering();
  return {
    account: toAccount(record),
    renumbered: accountRecords
      .filter((candidate) => candidate.id !== id && before.get(candidate.id) !== after.get(candidate.id))
      .map((candidate) => toAccount(candidate)),
  };
}

export const accountsMockResponses: MockResponse[] = [
  {
    state: ResponseStateEnum.SUCCESS,
    method: 'POST',
    path: ACCOUNTS_PATH,
    response: { status: 201, body: handleCreateAccount },
  },
  {
    state: ResponseStateEnum.SUCCESS,
    method: 'GET',
    path: ACCOUNTS_PATH,
    query: { format: 'csv' },
    response: { status: 200, body: handleAccountsExport },
  },
  {
    state: ResponseStateEnum.SUCCESS,
    method: 'GET',
    path: ACCOUNTS_PATH,
    response: { status: 200, body: handleGetAccounts },
  },
  {
    state: ResponseStateEnum.SUCCESS,
    method: 'PATCH',
    path: /^\/cru\/ledger\/accounts\/\d+\/$/,
    response: { status: 200, body: handleUpdateAccount },
  },
  {
    state: ResponseStateEnum.SUCCESS,
    method: 'GET',
    path: `${LEDGER_PATH}/exports/${ACTIVE_EXPORT_ID}/`,
    response: { status: 200, body: () => buildReadyExport(ACTIVE_EXPORT_ID, false) },
  },
  {
    state: ResponseStateEnum.SUCCESS,
    method: 'GET',
    path: `${LEDGER_PATH}/exports/${ARCHIVED_EXPORT_ID}/`,
    response: { status: 200, body: () => buildReadyExport(ARCHIVED_EXPORT_ID, true) },
  },
];
